import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import { syncConfigApi } from "../../../api/adminApi";
import {
  useSyncRunFiles,
  useSyncRuns,
} from "../../../api/queries/adminQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { downloadTextFile, toCsv } from "../../../utils/csv";
import { formatDateTime } from "../../../utils/dateTime";
import {
  FILE_CSV_HEADER,
  FILE_FILTERS,
  FILE_STATUS,
  RUN_STATUS,
  fileCsvRow,
  isStoppedRow,
  issueLabel,
  movedText,
  runDuration,
  shortId,
  triggerText,
} from "./syncFormat";

const EMPTY = [];
const RUNS_PER_PAGE = 20;
const CSV_PAGE = 200;

const copy = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied.`);
  } catch {
    toast.error("Could not copy. Select it and copy it by hand.");
  }
};

const Pill = ({ meta, value }) => (
  <span className={`status-pill status-pill--${meta?.tone || "gray"}`}>
    {meta?.label || value}
  </span>
);

const Counts = ({ item }) => (
  <span className="tabular-nums text-nowrap">
    {item.totalFiles ?? 0} files · {item.linked ?? 0} linked ·{" "}
    <span className={item.failed ? "text-danger fw-semibold" : ""}>
      {item.failed ?? 0} failed
    </span>{" "}
    ·{" "}
    <span className={item.notMoved ? "text-warning-emphasis fw-semibold" : ""}>
      {item.notMoved ?? 0} not moved
    </span>
  </span>
);

/** One folder of a run. Its status can be FAILED while files inside were linked. */
const FolderRows = ({ folders }) => (
  <div className="table-responsive mb-3">
    <table className="table table-sm mb-0 fs-080">
      <thead>
        <tr>
          <th>Branches</th>
          <th>Source folder</th>
          <th>Target folder</th>
          <th>Status</th>
          <th>Counts</th>
        </tr>
      </thead>
      <tbody>
        {folders.map((f) => (
          <tr key={f.logId || f.folderId}>
            <td className="fw-semibold">
              {(f.branches || []).join(", ") || "—"}
            </td>
            <td className="font-monospace fs-070">{f.folderId || "—"}</td>
            <td className="font-monospace fs-070">{f.targetFolderId || "—"}</td>
            <td>
              <Pill meta={RUN_STATUS[f.status]} value={f.status} />
            </td>
            <td>
              <Counts item={f} />
            </td>
          </tr>
        ))}
        {folders.length === 0 && (
          <tr>
            <td colSpan={5} className="text-slate-500">
              No folders yet.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

/** Every file of a run: server-side filter, search and paging. */
const RunFiles = ({ taskId, run }) => {
  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchText.trim()), 300);
    return () => clearTimeout(t);
  }, [searchText]);
  useEffect(() => setPage(1), [filter, search]);

  const filterParams = FILE_FILTERS.find((f) => f.key === filter).params;
  const query = useSyncRunFiles(
    taskId,
    run.runId,
    {
      ...filterParams,
      search: search || undefined,
      limit,
      skip: (page - 1) * limit,
    },
    { revision: run.status, live: run.status === "IN_PROGRESS" },
  );
  const allRows = query.data?.data || EMPTY;
  const stopped = allRows.filter(isStoppedRow);
  const rows = allRows.filter((r) => !isStoppedRow(r));

  const downloadCsv = async () => {
    setExporting(true);
    try {
      const out = [];
      for (let skip = 0; ; skip += CSV_PAGE) {
        const res = await syncConfigApi.runFiles(taskId, run.runId, {
          ...filterParams,
          search: search || undefined,
          limit: CSV_PAGE,
          skip,
        });
        const batch = res.data || EMPTY;
        out.push(...batch);
        if (!batch.length || out.length >= (res.total ?? 0)) break;
      }
      downloadTextFile(
        toCsv(
          FILE_CSV_HEADER,
          out.filter((r) => !isStoppedRow(r)).map(fileCsvRow),
        ),
        `zoho-sync-${shortId(run.runId)}-${filter}.csv`,
      );
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Could not download the files."));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      {stopped.map((r) => (
        <div key={r.id} className="alert alert-danger fs-080 py-2" role="alert">
          <strong>
            {(r.folderBranches || []).join(", ") || "A folder"}: the sync
            stopped.
          </strong>{" "}
          {r.issue}
        </div>
      ))}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div
          className="btn-group btn-group-sm"
          role="group"
          aria-label="Filter"
        >
          {FILE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`btn ${filter === f.key ? "btn-primary" : "btn-outline-secondary"}`}
              aria-pressed={filter === f.key}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <input
          type="search"
          className="form-control form-control-sm w-auto flex-grow-1"
          placeholder="File name, employee or number"
          aria-label="Search files"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary text-nowrap"
          onClick={downloadCsv}
          disabled={exporting || !query.data?.total}
        >
          <i className="bi bi-download me-1"></i>
          {exporting ? "Preparing…" : "CSV"}
        </button>
      </div>

      {query.isError ? (
        <div className="alert alert-danger fs-080 py-2">
          {getApiErrorMessage(query.error, "Could not load the files.")}
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm mb-0 fs-080">
            <thead>
              <tr>
                <th>File</th>
                <th>Employee</th>
                <th>Employee branch</th>
                <th>Source</th>
                <th>Document</th>
                <th>Result</th>
                <th>Moved</th>
                <th>Issue</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="text-break">
                    {r.fileName || "-"}
                    <div>
                      <Pill meta={FILE_STATUS[r.status]} value={r.status} />
                    </div>
                  </td>
                  <td>
                    {r.employee || "-"}
                    {r.employeeNumber && (
                      <div className="fs-070 text-slate-500">
                        {r.employeeNumber}
                      </div>
                    )}
                  </td>
                  <td>{r.employeeBranch || "-"}</td>
                  <td>{(r.folderBranches || []).join(", ") || "-"}</td>
                  <td>{r.document || "-"}</td>
                  <td>{r.result || "-"}</td>
                  <td>{movedText(r.moved)}</td>
                  <td>
                    {r.issueType && (
                      <div className="fw-semibold">
                        {issueLabel(r.issueType)}
                      </div>
                    )}
                    <div className="fs-070 text-slate-600 text-break">
                      {r.issue || (r.issueType ? "" : "-")}
                    </div>
                  </td>
                </tr>
              ))}
              {!query.isLoading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-slate-500 py-3">
                    No files here. Per-file details are kept for 30 days.
                  </td>
                </tr>
              )}
              {query.isLoading && (
                <tr>
                  <td colSpan={8} className="text-center py-3">
                    <span
                      className="spinner-border spinner-border-sm text-primary"
                      role="status"
                      aria-label="Loading"
                    ></span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      <Pagination
        page={page}
        limit={limit}
        total={query.data?.total || 0}
        onPageChange={setPage}
        onLimitChange={(l) => {
          setLimit(l);
          setPage(1);
        }}
        pageSizes={[50, 100, 200]}
      />
    </>
  );
};

const RunDetails = ({ taskId, run }) => (
  <div className="sync-run-details">
    {run.unauthorized > 0 && (
      <div className="alert alert-danger fs-080 py-2" role="alert">
        Zoho refused access (401/403) on {run.unauthorized} file
        {run.unauthorized === 1 ? "" : "s"}. Check the Zoho account can write to
        these folders.
      </div>
    )}
    <FolderRows folders={run.folders || EMPTY} />
    <RunFiles taskId={taskId} run={run} />
  </div>
);

/**
 * A task's log: its runs, each with folders and files. pollUntil keeps checking
 * for a moment after Run Now, before the new run has shown up.
 */
const SyncTaskLogs = ({ config, pollUntil, onClose }) => {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(null); // null = the newest; "none" = all closed
  const query = useSyncRuns(
    config.id,
    { limit: RUNS_PER_PAGE, skip: (page - 1) * RUNS_PER_PAGE },
    { pollUntil },
  );
  const runs = query.data?.data || EMPTY;
  const task = query.data?.task;
  const openId = expanded ?? runs[0]?.runId;
  const taskId = task?.id || config.id;

  return (
    <Dialog
      title={`Logs: ${task?.name || config.name}`}
      subtitle={task?.companyName || undefined}
      onClose={onClose}
      size="xl"
    >
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 fs-080">
        <span>
          Task ID: <code>{taskId}</code>
          <button
            type="button"
            className="btn btn-link btn-sm p-0 ms-2"
            onClick={() => copy(taskId, "Task ID")}
          >
            Copy
          </button>
        </span>
        <span className="text-slate-500">
          Per-file details are kept for 30 days.
        </span>
      </div>

      {query.isError && (
        <div className="alert alert-danger fs-085 py-2">
          {getApiErrorMessage(query.error, "Could not load the logs.")}
        </div>
      )}
      {query.isLoading && (
        <div className="text-center py-5">
          <span
            className="spinner-border text-primary"
            role="status"
            aria-label="Loading"
          ></span>
        </div>
      )}
      {query.isSuccess && runs.length === 0 && (
        <div className="approval-empty">
          <i className="bi bi-journal-text"></i>
          No runs logged yet. Press Run Now or wait for the schedule.
        </div>
      )}

      {runs.length > 0 && (
        <div className="table-responsive">
          <table className="table mb-0 fs-085">
            <thead>
              <tr>
                <th aria-label="Show details"></th>
                <th>Run</th>
                <th>Started</th>
                <th>Took</th>
                <th>Started by</th>
                <th>Status</th>
                <th>Counts</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => {
                const isOpen = openId === run.runId;
                return (
                  <React.Fragment key={run.runId}>
                    <tr className={isOpen ? "att-row-open" : ""}>
                      <td>
                        <button
                          type="button"
                          className="btn btn-sm btn-light border btn-icon-28"
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? "Hide" : "Show"} run details`}
                          onClick={() =>
                            setExpanded(isOpen ? "none" : run.runId)
                          }
                        >
                          <i
                            className={`bi bi-chevron-${isOpen ? "down" : "right"}`}
                          ></i>
                        </button>
                      </td>
                      <td className="text-nowrap">
                        <code title={run.runId}>{shortId(run.runId)}</code>
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-0 ms-1"
                          aria-label="Copy run ID"
                          onClick={() => copy(run.runId, "Run ID")}
                        >
                          <i className="bi bi-clipboard"></i>
                        </button>
                      </td>
                      <td className="text-nowrap">
                        {formatDateTime(run.startedAt)}
                      </td>
                      <td className="text-nowrap">
                        {runDuration(run.startedAt, run.finishedAt) || "—"}
                      </td>
                      <td>{triggerText(run)}</td>
                      <td>
                        <Pill
                          meta={RUN_STATUS[run.status]}
                          value={run.status}
                        />
                        {run.unauthorized > 0 && (
                          <div className="fs-070 text-danger mt-1">
                            <i className="bi bi-exclamation-triangle me-1"></i>
                            Zoho refused {run.unauthorized}
                          </div>
                        )}
                      </td>
                      <td>
                        <Counts item={run} />
                      </td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td></td>
                        <td colSpan={6} className="bg-light">
                          <RunDetails taskId={taskId} run={run} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {(query.data?.total || 0) > RUNS_PER_PAGE && (
        <Pagination
          page={page}
          limit={RUNS_PER_PAGE}
          total={query.data.total}
          onPageChange={(p) => {
            setPage(p);
            setExpanded(null);
          }}
          onLimitChange={() => {}}
          pageSizes={[RUNS_PER_PAGE]}
        />
      )}
    </Dialog>
  );
};

export default SyncTaskLogs;
